import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Container,
  ToggleButton,
  Header,
  Title,
  Stats,
  Stat,
  StatLabel,
  StatValue,
  Content,
  TranslationItem,
  TranslationKey,
  TranslationMeta,
  Badge,
  AutoTranslateInfo,
  ClearButton,
  EmptyState,
} from './MIssingTranslation.style'

interface MissingTranslation {
  key: string
  namespace: string
  language: string
  timestamp: number
}

const MissingTranslationsPanel = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [missingTranslations, setMissingTranslations] = useState<
    MissingTranslation[]
  >([])
  const { i18n } = useTranslation()

  useEffect(() => {
    // Track missing keys
    const handleMissingKey = (
      lngs: readonly string[],
      namespace: string,
      key: string
    ) => {
      const newMissing: MissingTranslation = {
        key: `${namespace}:${key}`,
        namespace,
        language: lngs[0] || i18n.language,
        timestamp: Date.now(),
      }

      // Defer state update to avoid "setState during render" warning
      // when t() is called with missing key during component render
      queueMicrotask(() => {
        setMissingTranslations((prev) => {
          // Avoid duplicates
          const exists = prev.some(
            (t) =>
              t.key === newMissing.key && t.language === newMissing.language
          )
          if (exists) return prev
          return [...prev, newMissing]
        })
      })
    }

    // Set up the missing key handler
    i18n.on('missingKey', handleMissingKey)

    return () => {
      i18n.off('missingKey', handleMissingKey)
    }
  }, [i18n])

  const uniqueLanguages = [
    ...new Set(missingTranslations.map((t) => t.language)),
  ]
  const uniqueNamespaces = [
    ...new Set(missingTranslations.map((t) => t.namespace)),
  ]

  // Check if auto-translate is available (you can customize this check)
  const isAutoTranslateEnabled =
    !!window.localStorage.getItem('lingo-dev-enabled')

  const clearMissingTranslations = () => {
    setMissingTranslations([])
  }

  // Only render in development
  if (process.env.NODE_ENV !== 'development') {
    return null
  }

  return (
    <>
      <ToggleButton
        $isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        title="Missing Translations"
      >
        {missingTranslations.length > 0 && !isOpen && (
          <span>{missingTranslations.length}</span>
        )}
        {isOpen ? 'Close' : 'Missing Keys'}
      </ToggleButton>

      <Container $isOpen={isOpen}>
        <Header>
          <Title>Missing Translations</Title>
          <Stats>
            <Stat>
              <StatLabel>Total Missing</StatLabel>
              <StatValue>{missingTranslations.length}</StatValue>
            </Stat>
            <Stat>
              <StatLabel>Languages</StatLabel>
              <StatValue>{uniqueLanguages.length}</StatValue>
            </Stat>
            <Stat>
              <StatLabel>Namespaces</StatLabel>
              <StatValue>{uniqueNamespaces.length}</StatValue>
            </Stat>
          </Stats>
        </Header>

        <Content>
          {isAutoTranslateEnabled && (
            <AutoTranslateInfo>
              ✓ Auto-translation is enabled via Lingo.dev
            </AutoTranslateInfo>
          )}

          {missingTranslations.length > 0 && (
            <div style={{ marginBottom: '16px', textAlign: 'right' }}>
              <ClearButton onClick={clearMissingTranslations}>
                Clear All
              </ClearButton>
            </div>
          )}

          {missingTranslations.length === 0 ? (
            <EmptyState>
              <p>🎉 No missing translations!</p>
              <p style={{ fontSize: '12px', marginTop: '8px' }}>
                Keys will appear here when they&apos;re accessed but not found.
              </p>
            </EmptyState>
          ) : (
            missingTranslations.map((translation, index) => (
              <TranslationItem
                key={`${translation.key}-${translation.language}-${index}`}
              >
                <TranslationKey>{translation.key}</TranslationKey>
                <TranslationMeta>
                  <Badge $type="language">
                    {translation.language.toUpperCase()}
                  </Badge>
                  <Badge $type="namespace">{translation.namespace}</Badge>
                  <span>
                    {new Date(translation.timestamp).toLocaleTimeString()}
                  </span>
                </TranslationMeta>
              </TranslationItem>
            ))
          )}
        </Content>
      </Container>
    </>
  )
}

export default MissingTranslationsPanel
